/**
 * Debug utility to track canvas CORS errors
 * 
 * Enable this in development to see where canvas errors are coming from.
 * Set DEBUG_CANVAS=true in console to enable.
 */

// getContext('2d') returns the same context object per canvas, so the wrap
// below must be applied at most once per context — re-wrapping chains the
// wrappers and a long session recurses drawImage into a stack overflow (the
// exact failure suppressCanvasErrors' removed patch caused in production).
const wrappedContexts = new WeakSet<CanvasRenderingContext2D>();
let canvasDebugEnabled = false;

export function enableCanvasDebug() {
  // @ts-ignore
  if (!window.DEBUG_CANVAS) return;
  if (canvasDebugEnabled) return;
  canvasDebugEnabled = true;

  const originalToDataURL = HTMLCanvasElement.prototype.toDataURL;
  const originalToBlob = HTMLCanvasElement.prototype.toBlob;
  const originalGetContext = HTMLCanvasElement.prototype.getContext;

  HTMLCanvasElement.prototype.toDataURL = function(...args) {
    console.log('🎨 toDataURL called:', {
      width: this.width,
      height: this.height,
      stack: new Error().stack,
    });
    
    try {
      return originalToDataURL.apply(this, args);
    } catch (error) {
      console.error('❌ toDataURL error:', error, {
        width: this.width,
        height: this.height,
        stack: new Error().stack,
      });
      throw error;
    }
  };

  HTMLCanvasElement.prototype.toBlob = function(callback, ...args) {
    console.log('🎨 toBlob called:', {
      width: this.width,
      height: this.height,
      stack: new Error().stack,
    });
    
    try {
      return originalToBlob.call(this, callback, ...args);
    } catch (error) {
      console.error('❌ toBlob error:', error, {
        width: this.width,
        height: this.height,
        stack: new Error().stack,
      });
      throw error;
    }
  };

  HTMLCanvasElement.prototype.getContext = function(contextId: any, options?: any): any {
    const context = originalGetContext.call(this, contextId, options);
    
    if (context && contextId === '2d' && !wrappedContexts.has(context as CanvasRenderingContext2D)) {
      wrappedContexts.add(context as CanvasRenderingContext2D);
      const ctx = context as CanvasRenderingContext2D;
      const originalDrawImage = ctx.drawImage;

      ctx.drawImage = function(image: any, ...args: any[]) {
        console.log('🎨 drawImage called:', {
          image,
          src: (image as any).src || (image as any).currentSrc || 'unknown',
          args,
          stack: new Error().stack,
        });
        
        try {
          return originalDrawImage.apply(this, [image, ...args] as any);
        } catch (error) {
          console.error('❌ drawImage error:', error, {
            image,
            src: (image as any).src || (image as any).currentSrc || 'unknown',
          });
          throw error;
        }
      };
    }
    
    return context;
  };

  console.log('🎨 Canvas debugging enabled. All canvas operations will be logged.');
  console.log('To disable, set window.DEBUG_CANVAS = false and reload.');
}

// Auto-enable if DEBUG_CANVAS is set
if (typeof window !== 'undefined') {
  // @ts-ignore
  if (window.DEBUG_CANVAS) {
    enableCanvasDebug();
  }
  
  // Make it available globally for manual enable
  // @ts-ignore
  window.enableCanvasDebug = enableCanvasDebug;
}
